import { Component, Vue } from "vue-property-decorator";
import { GLGlobal } from "commonui";
import { ApplicationViews, VcsMenuCategory, VcsMenuCategoryPermission } from "@/util/enum";
import { Paths } from "@/util/paths";
import { Path } from "vue-i18n";
import { AppMenuItem } from "@/util/constants";
import { ConversationTemplateLocale } from "@/locales/localeid";

@Component({
	components: {
		ChildNavigation: () =>
			import(/* webpackPrefetch: true */ "@/components/navigation/child-navigation/child-navigation.vue")
	}
})
export default class Templates extends Vue {
	public view = ApplicationViews.ConversationTemplates;
	public menuItems: AppMenuItem[] = [];
	public timeoutRef;

	created() {
		this.navigateToChild();
	}

	updated() {
		this.timeoutRef = setTimeout(() => {
			clearTimeout(this.timeoutRef);
			if (this.$route.path === Paths.ConversationTemplates) {
				this.navigateToChild();
			}
		}, 100);
	}

	navigateToChild() {
		const subPermissions = this.getSubPermissions();
		this.createMenuItems(subPermissions);

		const currentPath = this.$route.path;

		// If already a child route, navigate if user has permission.
		if (currentPath !== Paths.ConversationTemplates) {
			this.navigateToCurrentPath(currentPath, subPermissions);
			return;
		}

		// If parent route, then navigate to first permissible child route.
		if (subPermissions && subPermissions.length) {
			this.navigateToFirstPermissableTab(subPermissions[0]);
		} else {
			this.$router.push(Paths.Unauthorized);
		}
	}

	getSubPermissions() {
		const loginInfo = GLGlobal.loginInfo();
		const permissions = loginInfo.profile.permissions;

		const subPermissions = permissions.reduce((acc, permission) => {
			if (permission.items && permission.id === VcsMenuCategory.ListManagement) {
				var masterTemplatePermission = permission.items.find(
					(item) => item.id === VcsMenuCategory.ConversationTemplates && item.isVisible
				);

				if (masterTemplatePermission && masterTemplatePermission.items) {
					acc = acc
						.concat(
							masterTemplatePermission.items
								.filter((item) => item.isVisible)
								.map((item) => item.id as VcsMenuCategoryPermission)
						)
						.sort();
				}
			}

			return acc;
		}, [] as VcsMenuCategoryPermission[]);

		return subPermissions;
	}

	createMenuItems(subPermissions: VcsMenuCategoryPermission[]) {
		this.menuItems = subPermissions.map((permission) => {
			const item: AppMenuItem = { permissionId: permission };
			switch (permission) {
				case VcsMenuCategoryPermission.Fallback:
					item.path = Paths.Fallback;
					item.view = ApplicationViews.Fallback;
					item.localeId = ConversationTemplateLocale.TemplateTabFallback;
					break;
				case VcsMenuCategoryPermission.OpenClose:
					item.path = Paths.OpenClose;
					item.view = ApplicationViews.OpenClose;
					item.localeId = ConversationTemplateLocale.TemplateTabOpenClose;
					break;
				case VcsMenuCategoryPermission.UtteranceEntities:
					item.path = Paths.UtteranceEntities;
					item.view = ApplicationViews.UtteranceEntities;
					item.localeId = ConversationTemplateLocale.TemplateTabUtteranceEntities;
					break;
				case VcsMenuCategoryPermission.CommonIntents:
					item.path = Paths.CommonIntents;
					item.view = ApplicationViews.CommonIntents;
					item.localeId = ConversationTemplateLocale.TemplateTabCommonIntents;
					break;
				default:
					break;
			}

			return item;
		});
	}

	disablePath(path: string) {
		if (
			Paths.Fallback === path ||
			Paths.OpenClose === path ||
			Paths.UtteranceEntities ||
			Paths.CommonIntents
		) {
			return false;
		}

		return true;
	}

	navigateToCurrentPath(currentPath: Path, subPermissions: VcsMenuCategoryPermission[]) {
		let requiredPathPermission = 0;

		switch (currentPath) {
			case Paths.Fallback:
				requiredPathPermission = VcsMenuCategoryPermission.Fallback;
				break;

			case Paths.OpenClose:
				requiredPathPermission = VcsMenuCategoryPermission.OpenClose;
				break;

			case Paths.UtteranceEntities:
				requiredPathPermission = VcsMenuCategoryPermission.UtteranceEntities;
				break;

			case Paths.CommonIntents:
				requiredPathPermission = VcsMenuCategoryPermission.CommonIntents;
				break;

			default:
				break;
		}

		if (subPermissions.includes(requiredPathPermission)) {
			this.$router.push(currentPath);
		} else {
			this.$router.push(Paths.Unauthorized);
		}
	}

	navigateToFirstPermissableTab(firstPermission: VcsMenuCategoryPermission) {
		switch (firstPermission) {
			case VcsMenuCategoryPermission.Fallback:
				this.$router.push(Paths.Fallback);
				break;
			case VcsMenuCategoryPermission.OpenClose:
				this.$router.push(Paths.OpenClose);
				break;
			case VcsMenuCategoryPermission.UtteranceEntities:
				this.$router.push(Paths.UtteranceEntities);
				break;
			case VcsMenuCategoryPermission.CommonIntents:
				this.$router.push(Paths.CommonIntents);
				break;
			default:
				break;
		}
	}
}
